import { productTitle, productDelivery } from '../utils/productIdentity';
import { useState, useEffect } from 'preact/hooks';
import { t, resolveLocalizedString } from '../i18n';
import { getConfig, type CartLocalizedString, type CartPageSettings } from '../config';
import { getCart, removeFromCart, addCoupon, clearCoupon, setBonusProduct, applyCartResultToStore, type OrderLimits, type CartProductLimitMeta } from '../api';
import { store } from '../store';
import { navigateToView, openCatalogView } from '../navigation';
import { formatPrice } from '../utils/currency';
import { emit } from '../analytics';
import { CartQuantityControl } from './CartQuantityControl';

interface CartProduct extends CartProductLimitMeta {
  productid?: string;
  product_title?: string;
  made_in?: string;
  weight_value?: string;
  set_value?: string;
  amount?: number;
  product_price?: string;
  image_large_url?: string;
  [key: string]: unknown;
}

interface CartData {
  products?: CartProduct[];
  bonus?: CartProduct;
  bonus_mode?: 'none' | 'single' | 'choice' | string;
  bonus_selected_productid?: string | number;
  bonus_candidates?: CartProduct[];
  subtotal?: string | number;
  total?: string | number;
  discount?: number;
  discount_type?: string;
  discount_value?: string | number;
  bonus_discount?: string | number;
  shipping_cost?: number;
  shipping_method?: string;
  coupon?: string;
  coupon_discount?: string | number;
  coupon_type?: string;
  coupon_value?: string;
  order_limits?: OrderLimits;
}

const DEFAULT_SHIPPING_INSURANCE = 7;

function translateShippingMethod(method: unknown, fallback: string): string {
  const value = String(method ?? '').trim();
  if (!value) return fallback;
  const key = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return t(`shippingMethod.${key}`, value);
}

function productLineName(product: CartProduct): string {
  let name = productTitle(product);
  if (product.made_in) name += ` (${product.made_in})`;
  const pack = [product.weight_value, product.set_value].filter(Boolean).join(' ');
  if (pack) name += ` ${pack}`;
  const qty = Number(product.amount) || 1;
  if (qty > 1) name += ` ×${qty}`;
  return name;
}

function splitShipping(costRaw: unknown, insuranceAmount: number): { shipping: number; insurance: number } {
  const cost = Number(costRaw) || 0;
  const ins = Number.isFinite(insuranceAmount) && insuranceAmount > 0 ? insuranceAmount : 0;
  if (ins > 0 && cost > ins) {
    return { shipping: cost - ins, insurance: ins };
  }
  if (cost > 0) return { shipping: cost, insurance: 0 };
  return { shipping: 0, insurance: 0 };
}

function shopPath(base: string | undefined, path: string): string {
  const root = String(base || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return root ? `${root}${p}` : p;
}

function loc(val: CartLocalizedString | undefined, fallback: string): string {
  return resolveLocalizedString(val, fallback);
}

function normalizeIconKey(icon?: string): string {
  return String(icon || '')
    .trim()
    .toLowerCase()
    .replace(/^bi\s+bi-/, '')
    .replace(/^bi-/, '')
    .replace(/\s+/g, '-');
}

/** Tiny inline SVGs — no icon font in the widget bundle. */
function CartIcon({ name, size = 16 }: { name?: string; size?: number }) {
  const key = normalizeIconKey(name);
  const common = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    fill: 'currentColor',
    'aria-hidden': 'true' as const,
  };
  switch (key) {
    case 'trash':
    case 'trash3':
      return (
        <svg {...common}>
          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
          <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
        </svg>
      );
    case 'arrow-repeat':
    case 'arrow-clockwise':
    case 'arrow-counterclockwise':
      return (
        <svg {...common}>
          <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9" />
          <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
        </svg>
      );
    case 'x-circle':
    case 'x-circle-fill':
      return (
        <svg {...common}>
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z" />
        </svg>
      );
    case 'truck':
      return (
        <svg {...common}>
          <path d="M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85A1.5 1.5 0 0 1 16 8.118V13.5a1.5 1.5 0 0 1-1.5 1.5h-1a2 2 0 1 1-4 0H5a2 2 0 1 1-4 0H1.5A1.5 1.5 0 0 1 0 13.5zm1.5.5a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5H2a1 1 0 1 0 2 0h7a1 1 0 1 0 2 0h.5a.5.5 0 0 0 .5-.5V8.118a.5.5 0 0 0-.1-.312L13.02 5.95a.5.5 0 0 0-.39-.188H12V4a.5.5 0 0 0-.5-.5zM12 9.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1" />
        </svg>
      );
    case 'shield-check':
      return (
        <svg {...common}>
          <path d="M8 0c-.69 0-1.843.265-2.928.56-1.11.3-2.229.655-2.887.87a1.54 1.54 0 0 0-1.044 1.262c-.596 4.477.787 7.795 2.465 9.99a11.8 11.8 0 0 0 2.517 2.453c.386.273.744.482 1.048.625.28.132.581.24.829.24s.548-.108.829-.24a7 7 0 0 0 1.048-.625 11.8 11.8 0 0 0 2.517-2.453c1.678-2.195 3.061-5.513 2.465-9.99a1.54 1.54 0 0 0-1.044-1.263 63 63 0 0 0-2.887-.87C9.843.266 8.69 0 8 0m2.146 5.146a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793z" />
        </svg>
      );
    case 'headset':
      return (
        <svg {...common}>
          <path d="M8 1a5 5 0 0 0-5 5v1h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a6 6 0 1 1 12 0v6a2.5 2.5 0 0 1-2.5 2.5H9.366a1 1 0 0 1-.866.5h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 .866.5H11.5A1.5 1.5 0 0 0 13 12h-1a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1V6a5 5 0 0 0-5-5" />
        </svg>
      );
    case 'credit-card':
    case 'credit-card-2-front':
      return (
        <svg {...common}>
          <path d="M14 3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM2 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" />
          <path d="M2 5.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5m3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5m3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5m3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5" />
        </svg>
      );
    case 'bank':
      return (
        <svg {...common}>
          <path d="m8 0 6.61 3h.89a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5H15v7a.5.5 0 0 1 .485.38l.5 2a.496.496 0 0 1-.485.62H.5a.496.496 0 0 1-.485-.62l.5-2A.5.5 0 0 1 1 13V6H.5a.5.5 0 0 1-.5-.5v-2A.5.5 0 0 1 .5 3h.89zM3.777 3h8.447L8 1zM2 6v7h1V6zm2 0v7h2.5V6zm3.5 0v7h1V6zm2 0v7H12V6zM13 6v7h1V6zm2-1V4H1v1zm-.39 9H1.39l-.25 1h13.72z" />
        </svg>
      );
    case 'currency-bitcoin':
    case 'bitcoin':
      return (
        <svg {...common}>
          <path d="M5.5 13v1.25c0 .138.112.25.25.25h1a.25.25 0 0 0 .25-.25V13h.5v1.25c0 .138.112.25.25.25h1a.25.25 0 0 0 .25-.25V13h.084c1.992 0 3.416-1.033 3.416-2.82 0-1.502-1.007-2.323-2.186-2.44v-.088c.97-.242 1.683-.974 1.683-2.19C11.997 3.93 10.847 3 9.092 3H9V1.75a.25.25 0 0 0-.25-.25h-1a.25.25 0 0 0-.25.25V3h-.573V1.75a.25.25 0 0 0-.25-.25H5.75a.25.25 0 0 0-.25.25V3l-1.998.011a.25.25 0 0 0-.25.25v.769c0 .137.11.25.248.25l.846-.001a1.5 1.5 0 0 1 1.465 1.2l.022.098V12.3l-.02.098A1.5 1.5 0 0 1 4.595 13.7l-.847.001a.25.25 0 0 0-.248.25v.769c0 .137.11.25.25.25zm1.427-8.513h1.719c.906 0 1.438.498 1.438 1.312 0 .871-.575 1.362-1.877 1.362h-1.28zm0 4.051h1.84c1.137 0 1.756.58 1.756 1.524 0 .953-.626 1.45-2.158 1.45H6.927z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
          <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05" />
        </svg>
      );
  }
}

function noteLines(page: CartPageSettings | undefined): string[] {
  if (!Array.isArray(page?.notes)) return [];
  return page.notes
    .map((n) => loc(n, ''))
    .map((s) => s.trim())
    .filter(Boolean);
}


/** UKUS layout is for `#shop-cart-page` only; drawer/modal overlays stay classic. */
export function CartPage({ forceClassic = false }: { forceClassic?: boolean } = {}) {
  const config = getConfig();
  const isUkus = !forceClassic && config.cartSettings?.page_layout === 'ukus';
  const [cart, setCart] = useState<CartData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCart = async (opts?: { skipCache?: boolean; showLoading?: boolean }): Promise<CartData | null> => {
    const showLoading = opts?.showLoading !== false;
    if (showLoading) setLoading(true);
    const res = await getCart(config, { skipCache: opts?.skipCache ?? true });
    if (showLoading) setLoading(false);
    if (res.status === 200 && res.result) {
      const data = res.result as CartData;
      setCart(data);
      const count = data.products?.reduce((acc, p) => acc + (p.amount || 0), 0) || 0;
      const totalRaw = count === 0 ? 0 : parseFloat(String(data.total || '0').replace(/[^0-9.]/g, ''));
      store.setCart(count, totalRaw, data.products || []);
      return data;
    }
    return null;
  };

  useEffect(() => {
    const products = store.getCartProducts();
    if (products.length) {
      setCart({ products, total: store.getCartTotal() } as CartData);
      setLoading(false);
    }
    void loadCart({ skipCache: true, showLoading: !products.length });
    return store.subscribe(() => {
      const nextProducts = store.getCartProducts();
      if (!nextProducts.length) {
        setCart(null);
        return;
      }
      setCart((prev) => ({
        ...(prev ?? {}),
        products: nextProducts,
        total: store.getCartTotal(),
      } as CartData));
      setLoading(false);
    });
  }, []);

  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');

  const productMaxQty = (product: CartProduct): number => {
    const fromLine = Number(product.max_qty);
    if (Number.isFinite(fromLine) && fromLine > 0) return fromLine;
    const packUnits = Number(product.pack_units) || 1;
    const maxPills = Number(cart?.order_limits?.max_pills_per_product);
    const maxItems = Number(cart?.order_limits?.max_items_per_product);
    const byPills = Number.isFinite(maxPills) && maxPills > 0
      ? Math.max(1, Math.floor(maxPills / Math.max(1e-9, packUnits)))
      : 99;
    const byItems = Number.isFinite(maxItems) && maxItems > 0 ? maxItems : 99;
    return Math.min(byItems, byPills);
  };

  const productQtyLimitCode = (product: CartProduct): number =>
    Number(product.qty_limit_code) === 60103 ? 60103 : 60102;

  const productPillsLimitHint = (product: CartProduct): string | null => {
    if (productQtyLimitCode(product) !== 60103) return null;
    const pills = Number(product.max_pills_per_product) || Number(cart?.order_limits?.max_pills_per_product);
    const packs = productMaxQty(product);
    if (!Number.isFinite(pills) || pills <= 0) return null;
    return t('cartPage.maxPillsHint', { pills, packs });
  };

  const handleCartUpdated = (data: Record<string, unknown>) => {
    const next = data as CartData;
    setCart(next);
    applyCartResultToStore(store, next);
  };

  const handleAddCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponError('');
    setLoading(true);
    const res = await addCoupon(config, code);
    if (res.status === 200) {
      setCouponCode('');
      const updated = await loadCart();
      if (updated?.coupon) {
        store.addToast(t('cartPage.couponSuccess'), 'success');
      } else {
        store.addToast(t('cartPage.couponNotApplied'), 'info');
      }
    } else {
      setLoading(false);
      const msg = res.error || t('cartPage.couponInvalid');
      setCouponError(msg);
      store.addToast(msg, 'error');
    }
  };

  const handleClearCoupon = async () => {
    setLoading(true);
    const res = await clearCoupon(config);
    if (res.status === 200) {
      await loadCart();
      store.addToast(t('cartPage.couponCleared'), 'success');
    } else {
      setLoading(false);
      store.addToast(res.error || t('cartPage.removeFailed'), 'error');
    }
  };

  const handleRemove = async (productId: string) => {
    if (!productId) return;
    setLoading(true);
    const res = await removeFromCart(config, productId);
    if (res.status === 200) {
      emit('sw:remove-from-cart', { product_id: productId });
      store.addToast(t('cartPage.itemRemoved'), 'success');
      await loadCart();
    } else {
      setLoading(false);
      store.addToast(res.error || t('cartPage.removeFailed'), 'error');
    }
  };

  const handleRecalc = async () => {
    setLoading(true);
    await loadCart({ skipCache: true, showLoading: true });
  };

  const handleSelectBonus = async (bonusProductId: string) => {
    if (!bonusProductId) return;
    setLoading(true);
    const res = await setBonusProduct(config, bonusProductId);
    if (res.status === 200) {
      await loadCart();
    } else {
      setLoading(false);
      store.addToast(res.error || t('cartPage.removeFailed'), 'error');
    }
  };

  const lineTotal = (product: CartProduct): number => {
    const unit = Number(String(product.product_price ?? '0').replace(/[^0-9.-]/g, ''));
    const qty = Number(product.amount ?? 0);
    return unit * qty;
  };

  const goCheckout = () => {
    emit('sw:checkout-click', { cart_items: store.getCartProducts().length });
    if (store.getAuth()) {
      emit('sw:checkout-start');
      navigateToView('checkout');
    } else {
      emit('sw:checkout-auth-redirect', { target: 'checkout_auth' });
      store.setReturnTo('checkout');
      navigateToView('checkout-auth');
    }
  };

  const base = config.shopBaseUrl;
  const page = config.cartSettings?.page;
  const notes = noteLines(page);
  const showCoupon = page?.show_coupon !== false;
  const trustItems = Array.isArray(page?.trust) ? page!.trust! : [];
  const payItems = Array.isArray(page?.pay_items) ? page!.pay_items! : [];
  const helpHtml = loc(page?.help_html, '');
  const insuranceAmt = Number(page?.shipping_insurance);
  const shippingInsurance = Number.isFinite(insuranceAmt) && insuranceAmt >= 0
    ? insuranceAmt
    : DEFAULT_SHIPPING_INSURANCE;

  if (loading) {
    return (
      <div class="sw-tickets-shell">
        <div class="sw-surface-card text-center py-5">
          <span class="spinner-border text-primary" role="status"></span>
          <p class="mt-2 text-muted">{t('cartPage.loading')}</p>
        </div>
      </div>
    );
  }

  if (!cart || !cart.products || cart.products.length === 0) {
    if (isUkus) {
      return (
        <div class="sw-cart-ukus">
          <div class="sw-cart-ukus__empty">
            <p class="sw-cart-ukus__empty-title">{loc(page?.empty_title, t('cartPage.emptyTitle'))}</p>
            <p class="sw-cart-ukus__empty-lead">{loc(page?.empty_lead, t('cartPage.emptyLead'))}</p>
            <a href="/catalog" class="btn btn-primary sw-cart-ukus__btn-primary" onClick={(e) => { e.preventDefault(); openCatalogView(); }}>
              {t('cartPage.browseCatalog')}
            </a>
          </div>
        </div>
      );
    }
    return (
      <div class="sw-tickets-shell">
        <div class="sw-surface-card text-center py-5">
          <p class="fs-5 text-muted">{t('cartPage.empty')}</p>
          <a href="/catalog" onClick={(e) => { e.preventDefault(); openCatalogView(); }} class="btn btn-primary mt-2">
            {t('cartPage.startShopping')}
          </a>
        </div>
      </div>
    );
  }

  if (isUkus) {
    const { shipping, insurance } = splitShipping(cart.shipping_cost, shippingInsurance);
    const personalDiscount = (cart.discount || 0) > 0 && cart.discount_value;
    const showNotesCol = notes.length > 0 || showCoupon;

    return (
      <div class="sw-cart-ukus">
        <ul class="sw-cart-ukus__items">
          {cart.products.map((product, lineIndex) => (
            <li key={`${product.productid}-${lineIndex}`} class="sw-cart-ukus__item">
              <div class="sw-cart-ukus__item-main">
                <a
                  class="sw-cart-ukus__name"
                  href={product.productid ? shopPath(base, `/product/${product.productid}`) : '#'}
                  onClick={(e) => {
                    if (!product.productid) e.preventDefault();
                  }}
                >
                  {productLineName(product)}
                </a>
                <div class="sw-cart-ukus__price">{formatPrice(lineTotal(product))}</div>
              </div>
              <div class="sw-cart-ukus__item-actions">
                <button
                  type="button"
                  class="sw-cart-ukus__icon-btn"
                  title={t('cartPage.remove')}
                  aria-label={t('cartPage.remove')}
                  disabled={loading}
                  onClick={() => handleRemove(String(product.productid || ''))}
                >
                  <CartIcon name="trash3" size={16} />
                </button>
                <button
                  type="button"
                  class="sw-cart-ukus__icon-btn"
                  title={t('cartPage.recalculate')}
                  aria-label={t('cartPage.recalculate')}
                  disabled={loading}
                  onClick={() => void handleRecalc()}
                >
                  <CartIcon name="arrow-repeat" size={16} />
                </button>
              </div>
            </li>
          ))}

          {cart.bonus && (
            <li class="sw-cart-ukus__item sw-cart-ukus__item--bonus">
              <div class="sw-cart-ukus__item-main">
                <span class="sw-cart-ukus__name">{productLineName(cart.bonus)}</span>
                <div class="sw-cart-ukus__price sw-cart-ukus__price--free">{t('cartPage.free')}</div>
              </div>
              {cart.bonus_mode === 'choice' && Array.isArray(cart.bonus_candidates) && cart.bonus_candidates.length > 1 && (
                <select
                  class="form-select form-select-sm sw-cart-ukus__bonus-select"
                  value={String(cart.bonus_selected_productid || cart.bonus.productid || '')}
                  onChange={(e) => handleSelectBonus((e.target as HTMLSelectElement).value)}
                >
                  {cart.bonus_candidates.map((candidate) => (
                    <option key={String(candidate.productid || '')} value={String(candidate.productid || '')}>
                      {productTitle(candidate)}
                    </option>
                  ))}
                </select>
              )}
            </li>
          )}
        </ul>

        <div class={`sw-cart-ukus__grid${showNotesCol ? '' : ' sw-cart-ukus__grid--summary-only'}`}>
          {showNotesCol && (
            <aside class="sw-cart-ukus__notes">
              {notes.length > 0 && (
                <>
                  <h2 class="sw-cart-ukus__section-title">{loc(page?.notes_title, t('cartPage.quickNotes'))}</h2>
                  <ol class="sw-cart-ukus__notes-list">
                    {notes.map((html, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: html }} />
                    ))}
                  </ol>
                </>
              )}

              {showCoupon && (
                <form class="sw-cart-ukus__coupon" onSubmit={(e) => { e.preventDefault(); handleAddCoupon(); }}>
                  <label class="sw-cart-ukus__label" for="sw-cart-coupon">{t('cartPage.couponTitle')}</label>
                  <div class="sw-cart-ukus__coupon-row">
                    <input
                      id="sw-cart-coupon"
                      type="text"
                      class="form-control"
                      placeholder={t('cartPage.couponLabel')}
                      value={couponCode}
                      onInput={(e) => setCouponCode((e.target as HTMLInputElement).value)}
                    />
                    <button type="submit" class="btn btn-primary" disabled={!couponCode.trim() || loading}>
                      {t('cartPage.couponSubmit')}
                    </button>
                  </div>
                  {couponError && <div class="small text-danger mt-1">{couponError}</div>}
                </form>
              )}
            </aside>
          )}

          <section class="sw-cart-ukus__summary">
            <h2 class="sw-cart-ukus__section-title">{t('cartPage.orderSummary')}</h2>

            <div class="sw-cart-ukus__row">
              <span>{t('cartPage.subtotalLabel')}</span>
              <span>{formatPrice(cart.subtotal)}</span>
            </div>

            {personalDiscount ? (
              <div class="sw-cart-ukus__row">
                <span>{t('cartPage.personalDiscountLabel')}</span>
                <span>
                  {cart.discount_type === 'percent' || (!cart.discount_type && Number(cart.discount) <= 100)
                    ? `${cart.discount}% − `
                    : null}
                  {formatPrice(cart.discount_value)}
                </span>
              </div>
            ) : null}

            {Number(cart.bonus_discount) > 0 ? (
              <div class="sw-cart-ukus__row">
                <span>{t('cartPage.bonusDiscountLabel')}</span>
                <span>− {formatPrice(cart.bonus_discount)}</span>
              </div>
            ) : null}

            {cart.coupon_discount && cart.coupon_type !== 'free_ship' ? (
              <div class="sw-cart-ukus__row">
                <span>
                  {t('cartPage.couponLabelRow')}
                  {' '}
                  <a href="#" class="sw-cart-ukus__clear" title={t('cartPage.clearCoupon')} onClick={(e) => { e.preventDefault(); handleClearCoupon(); }}>
                    <CartIcon name="x-circle-fill" size={13} />
                  </a>
                </span>
                <span>
                  {cart.coupon_type === 'percent' && cart.coupon_value ? `${Math.floor(parseFloat(cart.coupon_value))}% − ` : null}
                  {formatPrice(cart.coupon_discount)}
                </span>
              </div>
            ) : null}

            <div class="sw-cart-ukus__row">
              <span>
                {t('cartPage.shippingLabel')}
                {cart.coupon_discount && cart.coupon_type === 'free_ship' ? (
                  <span class="sw-cart-ukus__muted">
                    {' '}({t('cartPage.discounted')}
                    <a href="#" class="sw-cart-ukus__clear" title={t('cartPage.clearCoupon')} onClick={(e) => { e.preventDefault(); handleClearCoupon(); }}>
                      <CartIcon name="x-circle-fill" size={13} />
                    </a>)
                  </span>
                ) : null}
              </span>
              <span>{shipping > 0 ? formatPrice(shipping) : t('cartPage.freeShort')}</span>
            </div>

            <div class="sw-cart-ukus__row">
              <span>{t('cartPage.shippingInsuranceLabel')}</span>
              <span>{insurance > 0 ? formatPrice(insurance) : t('cartPage.freeShort')}</span>
            </div>

            <div class="sw-cart-ukus__row sw-cart-ukus__row--total">
              <span>{t('cartPage.totalLabel')}</span>
              <span>{formatPrice(cart.total)}</span>
            </div>

            <div class="sw-cart-ukus__actions">
              <a
                href="/catalog"
                class="btn btn-outline-secondary sw-cart-ukus__btn-secondary"
                onClick={(e) => { e.preventDefault(); openCatalogView(); }}
              >
                {t('cartPage.backToShopping')}
              </a>
              <button type="button" class="btn btn-primary sw-cart-ukus__btn-primary" onClick={goCheckout}>
                {t('cartPage.checkout')}
              </button>
            </div>

            {(trustItems.length > 0 || payItems.length > 0) && (
              <div class="sw-cart-ukus__strips">
                {trustItems.length > 0 && (
                  <ul class="sw-cart-ukus__trust" aria-label="Why shop here">
                    {trustItems.map((item, i) => (
                      <li key={i}>
                        <CartIcon name={item.icon} size={18} />
                        <span>{loc(item.label, '')}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {payItems.length > 0 && (
                  <section class="sw-cart-ukus__pay" aria-label="Payment methods">
                    <h3 class="sw-cart-ukus__pay-title">{loc(page?.pay_title, t('cartPage.payTitle'))}</h3>
                    {loc(page?.pay_lead, '') && (
                      <p class="sw-cart-ukus__pay-lead">{loc(page?.pay_lead, '')}</p>
                    )}
                    <ul class="sw-cart-ukus__pay-list">
                      {payItems.map((item, i) => {
                        const html = loc(item.html, '');
                        return (
                          <li key={i}>
                            <CartIcon name={item.icon} size={18} />
                            {html
                              ? <span dangerouslySetInnerHTML={{ __html: html }} />
                              : <span>{loc(item.label, '')}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </section>
        </div>

        {helpHtml && (
          <details class="sw-cart-ukus__help">
            <summary>{loc(page?.help_title, t('cartPage.needHelp'))}</summary>
            <div class="sw-cart-ukus__help-body" dangerouslySetInnerHTML={{ __html: helpHtml }} />
          </details>
        )}
      </div>
    );
  }

  return (
    <div class="sw-tickets-shell">
    <div class="sw-surface-card shop-widget-cart-page p-3">
      <div class="table-responsive mb-4 d-none d-lg-block">
        <table class="table table-borderless align-top mb-0 sw-cart-lines-table" style={{ '--bs-table-bg': 'transparent' } as any}>
          <thead>
            <tr class="border-bottom" style={{ fontSize: 'var(--sw-font-size-sm)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--sw-muted)' }}>
              <th class="fw-normal pb-2 ps-0 text-start sw-cart-product-col">{t('cartPage.product')}</th>
              <th class="fw-normal pb-2 text-center sw-cart-qty-col">{t('cartPage.qty')}</th>
              <th class="fw-normal pb-2 pe-0 ps-2 ps-md-3 text-start sw-cart-pricing-col">
                <div class="d-flex justify-content-start align-items-end flex-nowrap gap-3 gap-md-4">
                  <span class="text-end text-nowrap sw-col-unit-price sw-cart-price-slot">{t('cartPage.price')}</span>
                  <span class="text-end text-nowrap sw-cart-price-slot">{t('cartPage.lineTotal')}</span>
                  <span class="flex-shrink-0" style={{ width: '1.75rem' }} aria-hidden="true" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {cart.products.map((product, lineIndex) => (
              <tr key={`${product.productid}-${lineIndex}`} class="border-bottom">
                <td class="ps-0 py-3 text-start sw-cart-product-col">
                  <div class="fw-semibold" style={{ lineHeight: 1.3 }}>
                    {productTitle(product)}
                    {product.made_in && <span class="fw-normal text-muted"> · {t('product.madeIn', 'Made in')}: {product.made_in}</span>}
                    <div class="small text-muted sw-product-delivery">{t('product.delivery', 'Delivery')}: {productDelivery(product)}</div>
                  </div>
                  {(product.weight_value || product.set_value) && (
                    <div class="small text-muted mt-1">
                      {[product.weight_value, product.set_value].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td class="text-center py-3 sw-cart-qty-col">
                  <CartQuantityControl
                    productId={String(product.productid || '')}
                    amount={Number(product.amount) || 1}
                    maxQty={productMaxQty(product)}
                    limitCode={productQtyLimitCode(product)}
                    disabled={loading}
                    onUpdated={handleCartUpdated}
                  />
                  {productPillsLimitHint(product) && (
                    <div class="small text-muted mt-1" style={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                      {productPillsLimitHint(product)}
                    </div>
                  )}
                </td>
                <td class="text-start py-3 pe-0 ps-2 ps-md-3 sw-cart-pricing-col">
                  <div class="d-flex align-items-center justify-content-start flex-nowrap gap-3 gap-md-4">
                    <span class="text-muted small text-nowrap text-end sw-col-unit-price sw-cart-price-slot">
                      {formatPrice(product.product_price)}
                    </span>
                    <span class="fw-semibold text-nowrap text-end sw-cart-price-slot">
                      {formatPrice(lineTotal(product))}
                    </span>
                    <button
                      type="button"
                      class="btn btn-link p-0 d-inline-flex sw-cart-remove-btn"
                      style={{ color: 'var(--sw-muted)' }}
                      title={t('cartPage.remove')}
                      onClick={() => handleRemove(product.productid || '')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {cart.bonus && (
              <tr class="border-bottom">
                <td class="ps-0 py-3 text-start sw-cart-product-col">
                  <div class="fw-semibold">{productTitle(cart.bonus)}</div>
                  {cart.bonus_mode === 'choice' && Array.isArray(cart.bonus_candidates) && cart.bonus_candidates.length > 1 && (
                    <div class="mt-2" style={{ maxWidth: '320px' }}>
                      <select
                        class="form-select form-select-sm"
                        value={String(cart.bonus_selected_productid || cart.bonus.productid || '')}
                        onChange={(e) => handleSelectBonus((e.target as HTMLSelectElement).value)}
                      >
                        {cart.bonus_candidates.map((candidate) => (
                          <option key={String(candidate.productid || '')} value={String(candidate.productid || '')}>
                            {productTitle(candidate)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {(cart.bonus.weight_value || cart.bonus.set_value) && (
                    <div class="small text-muted mt-1">
                      {[cart.bonus.weight_value, cart.bonus.set_value].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td class="text-center py-3 sw-cart-qty-col">
                  <span class="badge rounded-pill" style={{ background: 'var(--sw-border)', color: 'var(--sw-text)', fontWeight: 600, fontSize: 'var(--sw-font-size-sm)', padding: '0.3em 0.7em' }}>1</span>
                </td>
                <td class="text-start py-3 pe-0 ps-2 ps-md-3 sw-cart-pricing-col">
                  <div class="d-flex align-items-center justify-content-start flex-nowrap gap-3 gap-md-4">
                    <span class="small text-nowrap text-end sw-col-unit-price sw-cart-price-slot">—</span>
                    <span class="fw-semibold text-success text-nowrap text-end sw-cart-price-slot">{t('cartPage.free')}</span>
                    <span class="flex-shrink-0" style={{ width: '1.75rem' }} aria-hidden="true" />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div class="d-lg-none mb-4 d-grid gap-2">
        {cart.products.map((product, lineIndex) => (
          <div key={`mobile-${product.productid}-${lineIndex}`} class="rounded border p-2" style={{ borderColor: 'var(--sw-border)' }}>
            <div class="d-flex justify-content-between gap-2">
              <div class="fw-semibold" style={{ lineHeight: 1.3 }}>
                {productTitle(product)}
                {product.made_in && <span class="fw-normal text-muted"> · {t('product.madeIn', 'Made in')}: {product.made_in}</span>}
                <div class="small text-muted sw-product-delivery">{t('product.delivery', 'Delivery')}: {productDelivery(product)}</div>
              </div>
              <button
                type="button"
                class="btn btn-link p-0 d-inline-flex sw-cart-remove-btn"
                style={{ color: 'var(--sw-muted)' }}
                title={t('cartPage.remove')}
                onClick={() => handleRemove(product.productid || '')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                </svg>
              </button>
            </div>
            {(product.weight_value || product.set_value) && (
              <div class="small text-muted mt-1">
                {[product.weight_value, product.set_value].filter(Boolean).join(' · ')}
              </div>
            )}
            <div class="d-flex align-items-center justify-content-between mt-2 small gap-2">
              <span class="text-muted">{t('cartPage.qty')}</span>
              <CartQuantityControl
                productId={String(product.productid || '')}
                amount={Number(product.amount) || 1}
                maxQty={productMaxQty(product)}
                limitCode={productQtyLimitCode(product)}
                disabled={loading}
                compact
                onUpdated={handleCartUpdated}
              />
            </div>
            {productPillsLimitHint(product) && (
              <div class="small text-muted mt-1">{productPillsLimitHint(product)}</div>
            )}
            <div class="d-flex align-items-center justify-content-between mt-1 small">
              <span class="text-muted">{t('cartPage.price')}: <strong>{formatPrice(product.product_price)}</strong></span>
            </div>
            <div class="d-flex align-items-center justify-content-between mt-1">
              <span class="small text-muted">{t('cartPage.lineTotal')}</span>
              <span class="fw-semibold">{formatPrice(lineTotal(product))}</span>
            </div>
          </div>
        ))}
        {cart.bonus && (
          <div class="rounded border p-2" style={{ borderColor: 'var(--sw-border)' }}>
            <div class="fw-semibold">{productTitle(cart.bonus)}</div>
            <div class="small text-success mt-1">{t('cartPage.free')}</div>
          </div>
        )}
      </div>

      <div class="row">
        <div class="col-md-6 mb-4">
          <div class="bg-light p-3 fst-italic small mb-3 rounded">
            <p class="mb-1">{t('cartPage.quickNotes')}</p>
            <ul class="list-unstyled mb-0 ps-2">
              <li>{t('cartPage.note1')}</li>
              <li>{t('cartPage.note2')}</li>
              <li>{t('cartPage.note3')}</li>
              <li>{t('cartPage.note4')}</li>
            </ul>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleAddCoupon(); }}>
             <label class="form-label small">{t('cartPage.couponPlaceholder')}</label>
             <div class="input-group input-group-pill">
                <input
                  type="text"
                  class="form-control"
                  placeholder={t('cartPage.couponLabel')}
                  value={couponCode}
                  onInput={(e) => setCouponCode((e.target as HTMLInputElement).value)}
                />
                <button type="submit" class="btn btn-primary" disabled={!couponCode.trim()}>{t('cartPage.couponSubmit')}</button>
             </div>
             {couponError && <div class="small text-danger mt-1">{couponError}</div>}
          </form>
        </div>

        <div class="col-md-6 d-flex flex-column align-items-end">
          <table class="table table-sm table-borderless mb-0" style={{ width: 'auto', minWidth: '260px' }}>
            <tbody>
              <tr>
                <td class="text-muted pe-4">{t('cartPage.deliveryMethod')}</td>
                <td class="text-end">
                  <select class="form-select form-select-sm" style={{ width: 'auto' }} disabled>
                    <option>{translateShippingMethod(cart.shipping_method, t('cartPage.standard'))}</option>
                  </select>
                </td>
              </tr>
              <tr>
                <td class="text-muted pe-4">{t('cartPage.subtotal')}</td>
                <td class="text-end fw-medium">{formatPrice(cart.subtotal)}</td>
              </tr>

              {(cart.discount || 0) > 0 && cart.discount_value ? (
                <tr>
                  <td class="text-muted pe-4">{t('cartPage.personalDiscount')}</td>
                  <td class="text-end text-danger">-{formatPrice(cart.discount_value)}</td>
                </tr>
              ) : null}

              {Number(cart.bonus_discount) > 0 ? (
                <tr>
                  <td class="text-muted pe-4">{t('cartPage.bonusDiscount')}</td>
                  <td class="text-end text-success">-{formatPrice(cart.bonus_discount)}</td>
                </tr>
              ) : null}

              {cart.coupon_discount && cart.coupon_type !== 'free_ship' ? (
                <tr>
                  <td class="text-muted pe-4">
                    {t('cartPage.discountCoupon')}
                    {' '}
                    <a href="#" title={t('cartPage.clearCoupon')} onClick={(e) => { e.preventDefault(); handleClearCoupon(); }} style={{ color: 'var(--sw-danger)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/></svg>
                    </a>
                  </td>
                  <td class="text-end text-danger">
                    {cart.coupon_type === 'percent' && cart.coupon_value ? `${Math.floor(parseFloat(cart.coupon_value))}% − ` : null}
                    {formatPrice(cart.coupon_discount)}
                  </td>
                </tr>
              ) : null}

              <tr>
                <td class="text-muted pe-4">
                  {t('cartPage.shippingCost')}
                  {cart.coupon_discount && cart.coupon_type === 'free_ship' ? (
                    <>
                      {' '}
                      <a href="#" title={t('cartPage.clearCoupon')} onClick={(e) => { e.preventDefault(); handleClearCoupon(); }} style={{ color: 'var(--sw-danger)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/></svg>
                      </a>
                    </>
                  ) : null}
                </td>
                <td class="text-end fw-medium">
                  {cart.shipping_cost && cart.shipping_cost > 0
                    ? formatPrice(cart.shipping_cost - 7.0)
                    : t('cartPage.freeShipping')}
                </td>
              </tr>

              <tr>
                <td class="text-muted pe-4">{t('cartPage.shippingInsurance')}</td>
                <td class="text-end fw-medium">{formatPrice(7.00)}</td>
              </tr>

              <tr class="border-top">
                <td class="pt-2 pe-4 fw-semibold">{t('cartPage.total')}</td>
                <td class="pt-2 text-end fw-bold sw-ui-total" style={{ color: 'var(--sw-primary)' }}>{formatPrice(cart.total)}</td>
              </tr>
            </tbody>
          </table>

          <div class="d-flex flex-wrap gap-2 mt-3">
            <a href="/catalog" class="btn btn-outline-secondary btn-sm sw-ui-link" style="display:inline-flex;align-items:center;justify-content:center;line-height:var(--sw-line-height,1.5)" onClick={(e) => { e.preventDefault(); openCatalogView(); }}>
              {t('cartPage.backToShopping')}
            </a>
            <button class="btn btn-primary" onClick={goCheckout}>{t('cartPage.checkout')}</button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
